import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import {
  AppBar,
  Box,
  Dialog,
  Grid,
  IconButton,
  ListItemText,
  Toolbar,
  Typography,
} from "@mui/material";
import { StyledContainer } from "components/StyledContainer";
import StyledTextField from "components/StyledTextField";
import Transition from "components/Transition";
import { format } from "date-fns";
import { intlFormatDate } from "helpers";
import { FC, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { getJob, updateChecklistSpec, updateInspection } from "store/slices/jobs.store";
import { ChecklistType } from "operations/schema/schema";
import { cloneDeep } from "lodash";
import PrimaryButton from "components/PrimaryButton";
import { ChecklistRenderer } from "../ChecklistRenderer";
import {
  editSpecification,
  EditSpecificationPayload,
} from "store/slices/dialogs/editSpecification.mutation";

type InspectionDialogProps = {
  open: boolean;
  handleClose: () => void;
};

type EditableField =
  | "description"
  | "location"
  | "quantity"
  | "zone"
  | "detectorResistance"
  | "tamperResistance"
  | "voltageSignalReading";

export const InspectionDialog: FC<InspectionDialogProps> = ({ open, handleClose }) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const { selectedInspection: inspection } = useAppSelector((s) => s.jobs);
  const [checklistStateSpec, setChecklistStateSpec] = useState<ChecklistType | null>(null);
  const [editingField, setEditingField] = useState<EditableField | null>(null);
  const [fieldValue, setFieldValue] = useState<string | number>("");

  useEffect(() => {
    if (inspection?.specification.checklists?.length) {
      setChecklistStateSpec(cloneDeep(inspection.specification.checklists[0] ?? null));
    } else {
      setChecklistStateSpec(null);
    }
  }, [inspection?.specification.checklists]);

  const startEdit = (field: EditableField, currentValue: string | number) => {
    setEditingField(field);
    setFieldValue(currentValue);
  };

  const saveField = async (field: EditableField) => {
    if (!inspection) return;

    const valueToSend = field === "quantity" ? Number(fieldValue) : fieldValue;

    const updates: EditSpecificationPayload["updates"] = { [field]: valueToSend };
    const payload: EditSpecificationPayload = {
      specificationId: inspection.specification.id,
      updates,
    };

    try {
      await dispatch(editSpecification(payload));

      const updatedSpec = { ...inspection.specification, [field]: valueToSend };
      dispatch(updateInspection({ inspection: { ...inspection, specification: updatedSpec } }));

      await dispatch(getJob());
    } catch (err) {}
    setEditingField(null);
  };

  const saveChecklist = () => {
    if (!checklistStateSpec) return;
    dispatch(updateChecklistSpec({ checklist: checklistStateSpec }));
    handleClose();
  };

  const editableFields: EditableField[] = [
    "description",
    "location",
    "quantity",
    "zone",
    ...(inspection?.specification.isDetectorResistanceVisible
      ? ["detectorResistance" as EditableField]
      : []),
    ...(inspection?.specification.isTamperResistanceVisible
      ? ["tamperResistance" as EditableField]
      : []),
    ...(inspection?.specification.isVoltageSignalReadingVisible
      ? ["voltageSignalReading" as EditableField]
      : []),
  ];

  const renderEditableField = (field: EditableField, value: string | number) =>
    editingField === field ? (
      <StyledTextField
        value={fieldValue}
        onChange={(e) => setFieldValue(e.target.value)}
        onBlur={() => saveField(field)}
        onKeyDown={(e) => {
          if (e.key === "Enter") saveField(field);
        }}
        autoFocus
        size="small"
      />
    ) : (
      <Typography variant="body1">{value ?? "-"}</Typography>
    );

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      data-testid="inspectionDialog"
    >
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label={intl.formatMessage({ id: "general.back" })}
            size="large"
            data-testid="inspectionDialogCloseButton"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography className="AppBar.label" paddingRight="36px">
            <ListItemText
              primary={`${inspection?.specification.id} / ${inspection?.specification.description}`}
              sx={{ maxWidth: "80%" }}
            />
          </Typography>
        </Toolbar>
      </AppBar>
      <Toolbar />
      <StyledContainer>
        <Grid container direction="column" spacing={2}>
          {editableFields.map((field) => (
            <Grid
              key={field}
              item
              container
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box display="flex" alignItems="center">
                <Typography variant="body1" fontWeight={700} mr={1}>
                  <FormattedMessage id={`inspection.${field}`} />:
                </Typography>
                {renderEditableField(
                  field,
                  (inspection?.specification[field] as string | number) ?? "",
                )}
              </Box>
              <Box minWidth={40} textAlign="right">
                {field !== "description" && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (editingField === field) {
                        saveField(field);
                      } else {
                        startEdit(
                          field,
                          (inspection?.specification[field] as string | number) ?? "",
                        );
                      }
                    }}
                  >
                    {editingField === field ? (
                      <SaveIcon fontSize="small" />
                    ) : (
                      <EditIcon fontSize="small" />
                    )}
                  </IconButton>
                )}
              </Box>
            </Grid>
          ))}
          <Grid item container direction="row">
            <Grid item mr={1}>
              <Typography variant="body1" fontWeight={700}>
                <FormattedMessage id="inspection.dateInstalled" />:
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1">
                {inspection?.specification.dateInstalled
                  ? intlFormatDate(intl, inspection.specification.dateInstalled)
                  : "null"}
              </Typography>
            </Grid>
          </Grid>
          <Grid item>
            <Typography variant="body2">
              <span style={{ fontWeight: "bold" }}>Last Service: </span>
              <Box
                component="span"
                sx={{
                  fontWeight: "bold",
                  color:
                    inspection?.specification.resultOfLastService?.toLowerCase() === "pass"
                      ? "green"
                      : inspection?.specification.resultOfLastService?.toLowerCase() === "fail"
                        ? "red"
                        : "grey",
                }}
              >
                {inspection?.specification.resultOfLastService}
              </Box>
              {` on ${
                inspection?.specification.lastServiceDate
                  ? format(new Date(inspection.specification.lastServiceDate), "dd/MM/yyyy")
                  : "N/A"
              }`}
            </Typography>
          </Grid>
          <Grid item>
            <StyledTextField
              value={inspection?.notes || ""}
              label={intl.formatMessage({ id: "note" })}
              onChange={(e) => {
                if (!inspection) return;
                dispatch(
                  updateInspection({
                    inspection: { ...inspection, notes: e.target.value },
                  }),
                );
              }}
              multiline
              rows={15}
              data-testid="InspectionDialog-Text"
            />
          </Grid>
        </Grid>
      </StyledContainer>
      {checklistStateSpec && (
        <StyledContainer>
          <ChecklistRenderer
            checklist={checklistStateSpec}
            setChecklist={setChecklistStateSpec}
            onSave={saveChecklist}
            renderSaveButton={
              <PrimaryButton
                fullWidth
                onClick={saveChecklist}
                className="e2e-save-checklist-button"
                data-testid="ChecklistDialogSaveButton"
              >
                <FormattedMessage id="general.save" />
              </PrimaryButton>
            }
          />
        </StyledContainer>
      )}
    </Dialog>
  );
};
